import { useQuery } from '@tanstack/react-query';

import { Label } from '@nesso/ui/primitives/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@nesso/ui/primitives/select';
import { Textarea } from '@nesso/ui/primitives/textarea';

import {
    CANCELLATION_REASONS,
    type CancellationFeedback,
    OTHER_CANCELLATION_FEEDBACK,
    paidPlansOptions,
    subscriptionMeOptions,
} from '@/entities/subscription';
import { formatShortDate } from '@/shared/lib/time-utils';

type Props = {
    feedback: CancellationFeedback | null;
    onFeedbackChange: (feedback: CancellationFeedback) => void;
    comment: string;
    onCommentChange: (comment: string) => void;
};

export function CancelPlanFeedbackForm({
    feedback,
    onFeedbackChange,
    comment,
    onCommentChange,
}: Props) {
    const { data: subscription } = useQuery(subscriptionMeOptions());
    const { data: plans } = useQuery(paidPlansOptions());

    const currentPlan = plans?.find((plan) => plan.code === subscription?.planCode);
    const activeUntil = subscription?.currentPeriodEnd
        ? new Date(subscription.currentPeriodEnd)
        : undefined;
    const isOtherReason = feedback === OTHER_CANCELLATION_FEEDBACK;

    return (
        <div className="flex flex-col gap-6">
            <p className="text-lg font-medium">Cancellation</p>
            <div className="flex flex-col gap-1">
                <p className="text-base font-semibold">{currentPlan?.name ?? 'Current plan'}</p>
                {activeUntil && (
                    <p className="text-base text-muted-foreground">
                        Your plan stays active until {formatShortDate(activeUntil)}
                    </p>
                )}
            </div>
            <div className="flex flex-col gap-3">
                <Label htmlFor="cancellation-reason">Reason for cancellation</Label>
                <Select
                    value={feedback ?? undefined}
                    onValueChange={(value) => onFeedbackChange(value as CancellationFeedback)}
                >
                    <SelectTrigger id="cancellation-reason" className="w-full">
                        <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                        {CANCELLATION_REASONS.map((reason) => (
                            <SelectItem key={reason.value} value={reason.value}>
                                {reason.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {isOtherReason && (
                    <Textarea
                        aria-label="Reason for cancellation"
                        placeholder="Tell us more"
                        value={comment}
                        autoFocus
                        onChange={(event) => onCommentChange(event.target.value)}
                    />
                )}
            </div>
        </div>
    );
}
